/**
 * @fileoverview JkmultiselectComponent - 多選項選擇元件
 * @description
 * 此元件提供多項選擇功能，可通過開窗選擇多個項目並管理選擇結果。
 * 支援新增、刪除和匯入多個選項，並以表格形式顯示已選項目。
 *
 * @author JK Team
 * @version 1.0.0
 */
import { JkfieldlabelComponent } from 'jk-service';
import { MaterialModule } from 'jk-service';
import {
  Component,
  OnInit,
  input,
  output,
  viewChild,
  computed,
  ChangeDetectionStrategy,
  signal,
  inject,
  effect,
  Signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { JkBaseComponent } from 'jk-service';
import { AgOpenwinComponent } from '../agopenwin/agopenwin.component';
import { ConfirmDialogData } from 'jk-service';
import { JkaboutComponent } from 'jk-service';
import { jkLib } from 'jk-service';
import { idNameItem, JkOpenwinParams } from 'jk-service';
import { iif } from 'jk-service';

/**
 * @component JkmultiselectComponent
 * @description
 * 多選項選擇元件，提供通過開窗選擇多個項目的功能。
 * 選中的項目會以表格形式顯示，並支援刪除單個項目或清空所有項目。
 * @extends {JkBaseComponent}
 */
@Component({
  selector: 'app-jkmultiselect',
  templateUrl: './jkmultiselect.component.html',
  styleUrls: ['./jkmultiselect.component.scss'],
  standalone: true,
  imports: [MaterialModule, JkfieldlabelComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JkmultiselectComponent extends JkBaseComponent implements OnInit {
  override comType: string = 'jkmultiselect';

  /** 分頁器 */
  paginator = viewChild<MatPaginator>('paginator');

  /** 主資料表名稱 */
  maintable = input<string>('');

  /** 項目名稱 */
  item = input<string>('');

  /**
   * 資料來源
   * @type {MatTableDataSource<idNameItem>}
   */
  dataSource = input<MatTableDataSource<idNameItem>>();

  /**
   * 參數陣列
   * @type {any[]}
   */
  params = input<any[]>([]);

  /** 顯示表達式 */
  displayExpr = input<string>('name');

  /** 值表達式 */
  valueExpr = input<string>('id');

  // 使用 signals for reactive state
  private _values = signal<idNameItem[]>([]);

  /** 外部輸入的 values */
  values = input<idNameItem[]>([]);

  /**
   * 值變更事件輸出
   */
  valuesChange = output<idNameItem[]>();

  /**
   * 當前值的運算信號
   * @type {idNameItem[]}
   */
  currentValues = computed(() => this._values());

  /**
   * 字串值陣列的運算信號
   * @type {string[]}
   */
  stringValues: Signal<string[]> = computed(() => {
    const values = this._values();
    return values.map(item => item.id);
  });

  /**
   * Effect 來同步外部 input 與內部 signals
   */
  /*
  private syncValuesEffect = effect(() => {
    const inputValues = this.values();
    const currentInternal = this._values();

    // 只有在外部值與內部值不同時才更新
    if (!this.arraysEqual(inputValues, currentInternal)) {
      this._values.set([...inputValues]);
    }
  }); */

  /**
   * 比較兩個陣列是否相等
   */
  private arraysEqual(a: idNameItem[], b: idNameItem[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((item, index) =>
      item.id === b[index]?.id && item.name === b[index]?.name
    );
  }

  /**
   * 當值變更時觸發
   * @param {idNameItem[]} newValues - 新的值
   */
  onValuesChange(newValues: idNameItem[]) {
    this._values.set(newValues);
    const currentDataSource = this.dataSource();
    if (currentDataSource) {
      // 如果使用外部 dataSource，更新其數據
      currentDataSource.data = this._values();
    }
    // 注意：如果沒有外部 dataSource，internalDataSource 會自動透過 computed 信號更新
    this.valuesChange.emit(newValues);
  }

  /**
   * 推入新的值
   * @param {idNameItem[]} newValues - 新的值
   */
  pushValues(newValues: idNameItem[]) {
    try {
      const updatedValues = [];
      for (const newItem of newValues) {
          updatedValues.push(newItem);
      }
      this.onValuesChange(updatedValues);
    } catch (error) {
      console.error('pushValues error:', error);
    }
  }

  /**
   * View 初始化後執行
   */
  override ngAfterViewInit() {
    super.ngAfterViewInit();
    const currentDataSource = this.dataSource();
    if (currentDataSource && this.paginator()) {
      currentDataSource.paginator = this.paginator()!;
    }
    // 如果使用內部 dataSource，則設置分頁器
    else if (this.paginator()) {
      this.internalDataSource().paginator = this.paginator()!;
    }
  }

  /**
   * @property {string} importData - 要匯入的資料字串
   */
  importData: string = '';

  /**
   * @property {number} popupWidth - 彈出視窗寬度
   */
  popupWidth: number = 400;

  /** 表格顯示的欄位 */
  displayedColumns: string[] = ['id', 'name', 'btn'];

  /**
   * 內部使用的 dataSource，用於表格綁定
   * @type {MatTableDataSource<idNameItem>}
   */
  internalDataSource = computed(() => {
    const externalDataSource = this.dataSource();
    if (externalDataSource) {
      // 如果提供外部 dataSource，使用外部的
      return externalDataSource;
    } else {
      // 如果沒有提供外部 dataSource，使用內部管理的數據
      const ds = new MatTableDataSource<idNameItem>(this._values());
      if (this.paginator()) {
        ds.paginator = this.paginator()!;
      }
      return ds;
    }
  });

  /**
   * 取得選取的項目字串
   * @type {string}
   */
  selectedItems = computed((): string => {
    let result: string = '';
    const currentValues = this._values();
    for (let i = 0; i < currentValues.length; i++) {
      if(result.length > 0) result += ',';
      result += currentValues[i].id;
    }
    return result;
  });

  constructor() {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  /**
   * 開窗按鈕點擊事件
   * @param {any} e - 事件物件
   */
  doOpenWinClick(e: any) {
    let param: JkOpenwinParams = {
      isModal: true,
      maintable: this.maintable(),
      item: this.item(),
      params: this.params(),
      isMultiSelect: true,
      filterVisible: true,
      valueExpr: this.valueExpr(),
      displayExpr: this.displayExpr(),
      returnData: [],
    };
    const dialogRef = this.page.dialog.open(AgOpenwinComponent, this.page.initConfig_component(param));
    let retData: idNameItem[] = [];
    dialogRef.afterClosed().subscribe({
      next: (result: any) => {
      if (result.result) {
        try {
          let items = result.obj.returnData;
          if (items) {
            for (let i = 0; i < items.length; i++) {
              let e = items[i];
              let obj: idNameItem = {id:'',name:''};
              obj.id = e[this.valueExpr()];
              obj.name = e[this.displayExpr()];
              retData.push(obj);
            }
          }
          this.pushValues(retData);
        } catch (error: any) {
          alert('openwin 回傳錯誤:' + error);
        }
      }},
      error: (err) => {
        console.error('openwin error:', err);
      },
    });
  }

  /**
   * 刪除項目
   * @param {any} id - 要刪除的項目 ID
   */
  async onDelete(id: any) {
    this._values.update(current => {
      const ndx = current.findIndex((item) => item.id === id);
      if (ndx > -1) {
        current.splice(ndx, 1);
      }
      return [...current];
    });
    this.onValuesChange(this._values());
  }

  /**
   * 清除所有項目
   * @param {any} e - 事件物件
   */
  async onClear(e: any) {
    this._values.set([]);
    this.onValuesChange(this._values());
  }

  /**
   * 匯入資料
   * @param {any} e - 事件物件
   */
  async onImport(e: any) {
    let param: ConfirmDialogData = {
      title: '匯入資料',
      content: '',
      result: false,
    };
    const dialogRef = this.page.dialog.open(JkaboutComponent, { data: param });

    dialogRef.afterClosed().subscribe((result) => {
      if(!result) return;

      if (result) {
        try {
          let items = param.content.split('\n');
          if (items) {
            const newValues: idNameItem[] = [];
            for (let i = 0; i < items.length; i++) {
              let e = items[i];
              if (e.trim() !== '') {
                let obj:idNameItem = {id:'',name:''};
                obj.id= e;
                obj.name= '';
                newValues.push(obj);
              }
            }
            this._values.set(newValues);
          }
        } catch (error: any) {
          alert('openwin 回傳錯誤:' + error);
        }
      }
    });

    this.onValuesChange(this._values());
  }

  /**
   * 新增 Where 條件
   * @param {string} where - 現有的 where 條件
   * @param {string} fieldName - 欄位名稱
   * @param {string} [retFieldName='id'] - 回傳的欄位名稱
   * @returns {string} - 新的 where 條件
   */
  addWhere(where: string, fieldName: string, retFieldName: string = 'id') {
    const currentValues = this._values();
    if(this.required() && currentValues.length===0){
      let msg = this.label()+' 必須有值';
      alert(msg);
      throw new Error(msg);
    }
    where = jkLib.objectArray2Where(
      retFieldName,
      currentValues,
      where,
      fieldName
    );

    return where;
  }

  /**
   * 將值轉換為逗號分隔的字串
   * @returns {string} - 逗號分隔的字串
   */
  commaValues(): string {
    let result = '';
    const currentValues = this._values();
    for(let i=0;i<currentValues.length;i++){
      result = result + iif(result==='','',',')+ currentValues[i].id;
    }
    return result;
  }
}
